import type {
  GoogleMapOptions,
  GoogleMapsInstance,
  Hotel,
  LatLngBounds,
  Overlay,
  OverlayView,
} from '@/modules/hotels/components/MapView/models';

import {GoogleMap, useJsApiLoader} from '@react-google-maps/api';
import {
  type AsyncStatus,
  useFirstMountState,
  usePrevious,
} from '@react-hookz/web';
import {
  useBreakpointDown,
  useBreakpointUp,
  useCurrentBreakpoint,
} from '@travel-frontend/core';
import {keyBy, throttle} from 'lodash';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import {BackToHotelsButton} from '@/components/BackToHotelsButton';
import {MapContainer} from '@/components/MapContainer';
import {MapElements} from '@/components/MapElements';
import {MapNotification} from '@/components/MapNotification';
import {MapViewport} from '@/components/MapViewport';
import {config} from '@/config';
import {useLocalSplit} from '@/modules/common/hooks';
import {useSortParams} from '@/modules/common/hooks/useSortParams';
import {List} from '@/modules/hotels/components/List';
import {Wrapper} from '@/modules/hotels/components/List/components/Wrapper';
import {HotelInfoWindow} from '@/modules/hotels/components/MapView/components/HotelInfoWindow';
import {HotelPin} from '@/modules/hotels/components/MapView/components/HotelPin';
import {HotelTile} from '@/modules/hotels/components/MapView/components/HotelTile';
import {HotelTileInfoWindow} from '@/modules/hotels/components/MapView/components/HotelTileInfoWindow';
import {LoadingOverlay} from '@/modules/hotels/components/MapView/components/LoadingOverlay';
import {SplitView} from '@/modules/hotels/components/MapView/components/SplitView';
import {
  clusterHotels,
  createCanvasProjectionOverlay,
  getElementBounds,
  getLatLngBoundsForHotels,
  hotelToOffer,
} from '@/modules/hotels/components/MapView/utils';
import {PromotedHotel} from '@/modules/hotels/components/PromotedHotel';
import {useAppDispatch} from '@/redux/config';
import {selectHotelItemEvent} from '@/redux/middleware/googleTagManager';

// maximum number of hotels to render for each device-type/size
const HOTEL_LIMIT_MOBILE = 15;
const HOTEL_LIMIT_TABLET = 20;
const HOTEL_LIMIT_DESKTOP = 25;

// frequency in ms at which to update the viewport bounds while panning/zooming
const VIEWPORT_BOUNDS_REFRESH_RATE = 200;

type Props = {
  hotels: Array<Hotel>;
  isLoading: boolean;
  canHaveLoadingOverlay?: boolean;
  notification?: null | React.ReactNode;
  isNotificationVisible: boolean;
  initialSelectedHotelId?: string;
  tabRowEnabled?: boolean;
  onClose: () => void;
  filters: React.ReactNode;
  searchNode?: React.ReactNode;
  mapTrigger?: React.ReactNode;
  alternativeLayout?: boolean;
  splitView?: boolean;
  travellerCount: number;
  promotedHotel: Hotel | null;
  promotedHotelId: string;
  promotedHotelEnabled: boolean;
  promotedHotelStatus: AsyncStatus;
};

export const MapComponent: React.FunctionComponent<Props> = ({
  hotels: hotelsProperty,
  isLoading,
  canHaveLoadingOverlay = false,
  notification = null,
  isNotificationVisible = false,
  initialSelectedHotelId = '',
  tabRowEnabled = false,
  onClose,
  filters,
  searchNode,
  mapTrigger,
  alternativeLayout,
  splitView = false,
  travellerCount,
  promotedHotel,
  promotedHotelId,
  promotedHotelEnabled,
  promotedHotelStatus,
}) => {
  const currentBreakpoint = useCurrentBreakpoint();
  const smDown = useBreakpointDown('sm');
  const lgUp = useBreakpointUp('lg');
  const mdDown = currentBreakpoint === 'md' || smDown;
  const isFirstMount = useFirstMountState();
  const [sortParams] = useSortParams();
  const dispatch = useAppDispatch();

  const isHotelContentApiFeatureOn =
    useLocalSplit<'default' | 'off' | 'on'>('FFP_5733_hotel_content_api')
      .treatment === 'on';

  const hotels = useMemo(
    () =>
      promotedHotel ? hotelsProperty.concat(promotedHotel) : hotelsProperty,
    [hotelsProperty, promotedHotel]
  );

  const [mapInstance, setMapInstance] = useState<GoogleMapsInstance | null>(
    null
  );
  const [viewportBounds, setViewportBounds] = useState<LatLngBounds | null>(
    null
  );
  const [idleViewportBounds, setIdleViewportBounds] =
    useState<LatLngBounds | null>(null);
  const [canvasProjectionOverlay, setCanvasProjectionOverlay] =
    useState<OverlayView | null>(null);
  const [selectedHotelId, setSelectedHotelId] = useState('');
  const [lastSelectedHotelId, setLastSelectedHotelId] = useState('');
  const [collapse, setCollapse] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const previousIsLoading = usePrevious(isLoading);

  const containerElementRef = useRef<HTMLDivElement | null>(null);
  const mapViewportElementRef = useRef<HTMLDivElement | null>(null);

  // starts loading the google maps api once this component mounts
  const {isLoaded} = useJsApiLoader({
    ...config.googleMapsApiOptions,
    googleMapsApiKey: import.meta.env.VITE_SERVICE_GOOGLE_MAPS_KEY,
    language: import.meta.env.VITE_LOCALE,
  });

  const mapOptions = useMemo<GoogleMapOptions>(
    () =>
      isLoaded
        ? {
            mapId: '1e223fc4a0542101',
            clickableIcons: true,
            disableDefaultUI: mdDown,
            gestureHandling: mdDown || splitView ? 'greedy' : 'cooperative',
            zoomControlOptions: {
              position: google.maps.ControlPosition.RIGHT_BOTTOM,
            },
            minZoom: 2,
            mapTypeControl: true,
            mapTypeControlOptions: {
              mapTypeIds: [
                google.maps.MapTypeId.ROADMAP,
                google.maps.MapTypeId.SATELLITE,
              ],
              position: google.maps.ControlPosition.RIGHT_TOP,
            },
            // @FIXME why disable these? they are nice features :(
            fullscreenControl: false,
            streetViewControl: false,
            restriction: {
              latLngBounds: {north: 85, south: -85, west: -180, east: 180},
              strictBounds: true,
            },
          }
        : {},
    [isLoaded, mdDown, splitView]
  );

  const hotelTileStyle = smDown ? 'static' : 'infoWindow';
  let hotelRenderLimit = HOTEL_LIMIT_DESKTOP;
  if (smDown) {
    hotelRenderLimit = HOTEL_LIMIT_MOBILE;
  } else if (mdDown) {
    hotelRenderLimit = HOTEL_LIMIT_TABLET;
  }

  const hotelsById = useMemo(
    () => keyBy<Hotel>(hotels, (hotel) => hotel.id),
    [hotels]
  );
  const selectedHotel = hotelsById[selectedHotelId];

  const clusteredPointsInViewportBounds = useMemo(() => {
    // Ensure initial hotel is included in clustering
    const hotelsToCluster = hotels.filter(
      (hotel) => hotel.id !== initialSelectedHotelId
    );

    // It puts initial hotel pin on the top so it's always visible
    if (initialSelectedHotelId && hotelsById[initialSelectedHotelId]) {
      hotelsToCluster.unshift(hotelsById[initialSelectedHotelId]);
    }
    return clusterHotels({
      hotels: hotelsToCluster,
      viewportBounds,
      zoom: Math.floor(mapInstance?.getZoom() || 0),
      limit: hotelRenderLimit,
      radius: 69,
    });
  }, [
    hotels,
    viewportBounds,
    mapInstance,
    hotelRenderLimit,
    initialSelectedHotelId,
    hotelsById,
  ]);

  const clusteredPointsInIdleViewportBounds = useMemo(
    () =>
      clusterHotels({
        hotels,
        viewportBounds: idleViewportBounds,
        zoom: Math.floor(mapInstance?.getZoom() || 0),
        limit: hotelRenderLimit,
        radius: 69,
      }),
    [hotels, idleViewportBounds, mapInstance, hotelRenderLimit]
  );

  const hotelsToRenderAsPins = useMemo(() => {
    const hotelList = clusteredPointsInViewportBounds.map(
      (point) => hotelsById[point.id]
    );

    const initialHotel = initialSelectedHotelId
      ? hotelsById[initialSelectedHotelId]
      : null;

    // Ensure the initial hotel is always rendered
    if (initialHotel && !hotelList.includes(initialHotel)) {
      hotelList.push(initialHotel);
    }

    // selectedHotel pin should always be rendered
    if (selectedHotel && !hotelList.includes(selectedHotel)) {
      hotelList.push(selectedHotel);
    }

    return hotelList;
  }, [
    hotelsById,
    clusteredPointsInViewportBounds,
    initialSelectedHotelId,
    selectedHotel,
  ]);

  const hotelsToRenderInList = useMemo(() => {
    const itemsWithoutSelectedHotel = clusteredPointsInIdleViewportBounds
      .filter((point) => point.id !== lastSelectedHotelId)
      .map((point) => hotelsById[point.id]);

    const lastSelectedHotel = hotelsById[lastSelectedHotelId];
    return (
      (
        lastSelectedHotel
          ? [lastSelectedHotel, ...itemsWithoutSelectedHotel]
          : itemsWithoutSelectedHotel
      )
        // promoted hotel gets rendered seperately above,
        // so we remove it from the list here to prevent duplicates
        .filter((hotel) => hotel.id !== promotedHotel?.id)
    );
  }, [
    clusteredPointsInIdleViewportBounds,
    hotelsById,
    lastSelectedHotelId,
    promotedHotel?.id,
  ]);

  // #region ACTIONS

  const calculateViewportBounds = useCallback(() => {
    return getElementBounds({
      mapCanvasProjection: canvasProjectionOverlay?.getProjection(),
      element: mapViewportElementRef.current,
      container: containerElementRef.current,
    });
  }, [canvasProjectionOverlay]);

  const throttledUpdateViewportBounds = useMemo(
    () =>
      throttle(() => {
        setViewportBounds(calculateViewportBounds());
      }, VIEWPORT_BOUNDS_REFRESH_RATE),
    [calculateViewportBounds]
  );

  const panToBoundsIfNecessary = useCallback(
    (element?: null | HTMLElement) => {
      const bounds = getElementBounds({
        mapCanvasProjection: canvasProjectionOverlay?.getProjection(),
        element,
        container: mapViewportElementRef.current,
      });
      if (mapInstance && bounds) {
        mapInstance.panToBounds(bounds, {
          // to keep things simple, we keep these static.
          // just remember to update manually if necessary!
          top: 16,
          left: 16,
          right: splitView && lgUp ? 800 : 16,
          bottom: hotelTileStyle === 'static' ? 197 : 72,
        });
      }
    },
    [canvasProjectionOverlay, mapInstance, splitView, lgUp, hotelTileStyle]
  );

  // #region CALLBACKS

  const handleHotelPinSelected = useCallback(
    (id: string, event: google.maps.MapMouseEvent) => {
      event.stop();
      setSelectedHotelId((previousId: string) =>
        previousId === id
          ? ''
          : hotels.find((hotel) => hotel.id === id)?.id || ''
      );
      setLastSelectedHotelId(id);
    },
    [hotels, setSelectedHotelId]
  );

  const handleHotelInfoWindowLoaded = useCallback(
    (overlay: Overlay) => {
      panToBoundsIfNecessary(overlay.container);
    },
    [panToBoundsIfNecessary]
  );

  const handleHotelTileOrInfoWindowClick = useCallback(() => {
    const hotelIndex = hotels.findIndex(
      (hotel) => hotel.id === selectedHotelId
    );
    dispatch(
      selectHotelItemEvent.action({
        hotelId: selectedHotelId,
        position: hotelIndex,
        isMap: true,
        isHotelContentApiFeatureOn,
        isGallery: false,
        sortParams,
      })
    );
  }, [dispatch, hotels, selectedHotelId, sortParams]);

  // #region EFFECTS

  // the selected hotel may not be present anymore among all the hotels if they change,
  // deselect it if that is the case.
  useEffect(() => {
    if (
      selectedHotelId &&
      !hotels.some((hotel) => hotel.id === selectedHotelId)
    ) {
      setSelectedHotelId('');
      setLastSelectedHotelId('');
    }
  }, [selectedHotelId, hotels, setSelectedHotelId]);

  // select the initial hotel after loading or on mount
  useEffect(() => {
    if (
      !selectedHotelId &&
      hotels.length > 0 &&
      ((!isLoading && previousIsLoading) || isFirstMount)
    ) {
      setSelectedHotelId(initialSelectedHotelId);
      setLastSelectedHotelId(initialSelectedHotelId);
    }
  }, [
    selectedHotelId,
    hotels,
    isLoading,
    previousIsLoading,
    isFirstMount,
    initialSelectedHotelId,
    setSelectedHotelId,
  ]);

  // whenever the hotels change, extend the maps bounds to fit
  // @TODO bounds should include HotelInfoWindow if open!
  useEffect(() => {
    if (!mapInstance || hotels.length === 0) {
      return;
    }

    const bounds = getLatLngBoundsForHotels(hotels);

    const padding = smDown
      ? {left: 15, top: 0, bottom: 0, right: 15}
      : {left: 140, top: 50, bottom: 280, right: 140};

    mapInstance.fitBounds(bounds, padding);
  }, [mapInstance, hotels, smDown]);

  // The selected hotel pin may end up underneath the tile we open.
  // Pan the map in that case to make sure it stays visible.
  useLayoutEffect(() => {
    if (mapInstance && selectedHotelId) {
      // delay a tiny bit to correct for timing issues on initialization
      // (this is not the "right" way to fix this...)
      setTimeout(() => {
        panToBoundsIfNecessary(
          containerElementRef.current?.querySelector(
            '[data-selected-hotel-pin]'
          )
        );
      }, 50);
    }
  }, [mapInstance, selectedHotelId, panToBoundsIfNecessary]);

  useEffect(() => {
    // cancel any pending subscriptions when unmounting
    return () => {
      throttledUpdateViewportBounds.cancel();
    };
  }, [throttledUpdateViewportBounds]);

  useResizeObserver({
    ref: mapViewportElementRef,
    onResize: () => {
      // https://github.com/ZeeCoder/use-resize-observer/issues/38
      requestAnimationFrame(() => {
        setViewportBounds(calculateViewportBounds());
      });
    },
  });

  // #region RENDER
  return (
    <MapContainer ref={containerElementRef} data-testid="hotellist-map-layer">
      {isLoaded && (
        <GoogleMap
          options={mapOptions}
          mapContainerStyle={{
            position: 'absolute',
            inset: '0',
          }}
          onLoad={(googleMapInstance) => {
            setMapInstance(googleMapInstance);
            setCanvasProjectionOverlay(
              createCanvasProjectionOverlay(googleMapInstance)
            );
          }}
          onIdle={() => {
            const updatedViewportBounds = calculateViewportBounds();
            setViewportBounds(updatedViewportBounds);
            // for performance reasons we use a seperate bounds state as basis for the hotels we render in the split view list
            setIdleViewportBounds(updatedViewportBounds);
          }}
          onBoundsChanged={() => {
            throttledUpdateViewportBounds();
          }}
          onProjectionChanged={() => {
            setViewportBounds(calculateViewportBounds());
          }}
          onUnmount={() => {
            setMapInstance(null);
          }}
          onClick={() => {
            setSelectedHotelId('');
          }}
        >
          {hotelsToRenderAsPins.map((hotel) => (
            <HotelPin
              isPromoted={hotel.id === promotedHotel?.id}
              key={hotel.id}
              id={hotel.id}
              price={hotel.totalPriceDisplay}
              latitude={hotel.lat}
              longitude={hotel.lon}
              selected={hotel.id === selectedHotelId}
              // instead of dealing with refs and when we can read them,
              // we use this as simple indicator of the selected pin
              data-selected-hotel-pin={
                hotel.id === selectedHotelId || undefined
              }
              onSelect={handleHotelPinSelected}
              isHovered={hovered === hotel.id}
              splitView={splitView}
            />
          ))}
          {hotelTileStyle === 'infoWindow' &&
            selectedHotel &&
            (splitView && lgUp ? (
              <HotelTileInfoWindow
                id={selectedHotel.id}
                latitude={selectedHotel.lat}
                longitude={selectedHotel.lon}
                offer={hotelToOffer(selectedHotel, travellerCount)}
                onLoad={handleHotelInfoWindowLoaded}
                onClick={handleHotelTileOrInfoWindowClick}
              />
            ) : (
              <HotelInfoWindow
                hotelId={selectedHotel.id}
                latitude={selectedHotel.lat}
                longitude={selectedHotel.lon}
                offer={hotelToOffer(selectedHotel, travellerCount)}
                onLoad={handleHotelInfoWindowLoaded}
                onClick={handleHotelTileOrInfoWindowClick}
              />
            ))}
        </GoogleMap>
      )}
      {splitView && lgUp ? (
        <SplitView
          filters={filters}
          searchNode={searchNode}
          mapTrigger={mapTrigger}
          hotelsToRender={hotelsToRenderInList}
          collapse={collapse}
          setCollapse={setCollapse}
          mapViewportElementRef={mapViewportElementRef}
          alternativeLayout={alternativeLayout}
          visible={canHaveLoadingOverlay && mapInstance != null && isLoading}
          onClickBackToHotelsButton={onClose}
        >
          {promotedHotelEnabled && (
            <Wrapper
              onMouseEnter={() => {
                if (promotedHotel) {
                  setHovered(promotedHotel.id);
                }
              }}
              onMouseLeave={() => {
                setHovered(null);
              }}
              isHovered={hovered === promotedHotel?.id}
              style={{marginBottom: '24px'}}
            >
              <PromotedHotel
                id={promotedHotelId}
                hotel={promotedHotel}
                status={promotedHotelStatus}
                isVisuallyHidden={false}
                small
              />
            </Wrapper>
          )}
          <List
            mapViewActive={true}
            visuallyHidden={false}
            pinnedHotels={hotelsToRenderInList}
            hovered={hovered}
            onHover={setHovered}
            small={true}
            splitView={true}
          />
        </SplitView>
      ) : (
        <MapElements withDefaultControls={!mapOptions.disableDefaultUI}>
          <LoadingOverlay
            // @TODO only show overlay on updates, not initially
            visible={canHaveLoadingOverlay && mapInstance != null && isLoading}
          />
          {notification && (
            <MapNotification visible={isNotificationVisible}>
              {notification}
            </MapNotification>
          )}
          <MapViewport ref={mapViewportElementRef} />
          {hotelTileStyle === 'static' && selectedHotel && (
            <HotelTile
              id={selectedHotel.id}
              offer={hotelToOffer(selectedHotel, travellerCount)}
              onClickHandleTracking={handleHotelTileOrInfoWindowClick}
            />
          )}
          {(lgUp || !tabRowEnabled) && <BackToHotelsButton onClick={onClose} />}
        </MapElements>
      )}
    </MapContainer>
  );
};
